import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function BookingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/booking`,
      name: 'Book a Private Jet | YESJETS Charter Request',
      url: `${siteUrl}/booking`,
      description: 'Request a private jet charter quote with YESJETS. Select your route, date, and passengers. 24-hour response, transparent pricing, and 50+ luxury aircraft available worldwide.',
      isPartOf: { '@type': 'WebSite', '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Book a Flight', item: `${siteUrl}/booking` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/booking#service`,
      name: 'Private Jet Charter Booking',
      serviceType: 'Private Jet Charter',
      provider: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'YESJETS',
        url: siteUrl,
        telephone: '+17085678866',
        email: 'Flights@flyyesjets.com',
      },
      areaServed: { '@type': 'Place', name: 'Worldwide' },
      description: 'Request a private jet charter quote with YESJETS. Choose your departure city, destination, date, and passenger count. Our team responds within 24 hours.',
      url: `${siteUrl}/booking`,
    },
  ];

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', departure: '', destination: '',
    departureDate: '', departureTime: '', returnDate: '', returnTime: '',
    passengers: '1',
    luggageType: '', luggageCount: '0',
    catering: '',
    pets: '0',
    flexibility: '',
    specialRequirements: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://readdy.ai/api/form/d6o924t39lnhn4hh16ng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', departure: '', destination: '', departureDate: '', departureTime: '', returnDate: '', returnTime: '', passengers: '1', luggageType: '', luggageCount: '0', pets: '0', flexibility: '', specialRequirements: '' });
      } else { setSubmitStatus('error'); }
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Book a Private Jet | YESJETS Charter Request – Los Angeles"
        description="Request a private jet charter quote with YESJETS. Select your route, date, and passengers. 24-hour response, transparent pricing, and 50+ luxury aircraft available worldwide."
        keywords="book private jet, jet charter booking, private flight request, YESJETS booking, luxury aircraft reservation"
        canonical="/booking"
        jsonLd={jsonLd}
      />

      <Navbar />

      <div className="pt-16 md:pt-20 min-h-screen flex items-stretch">
        <div className="w-full flex flex-col lg:grid lg:grid-cols-5 min-h-[900px]">
          {/* Left Side - Image (hidden on mobile) */}
          <div className="hidden lg:block lg:col-span-2 relative">
            <img
              src="https://public.readdy.ai/ai/img_res/8c859e2d61e8cb90444568977bde7235.jpg"
              alt="Luxury Jet Interior"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:col-span-3 bg-charcoal px-6 sm:px-10 md:px-16 py-12 md:py-20 flex items-center">
            <div className="w-full max-w-2xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-serif font-semibold text-white mb-3">
                PLAN YOUR FLIGHT
              </h1>
              <div className="w-24 h-1 bg-gold mb-8 md:mb-10"></div>

              <form onSubmit={handleSubmit} data-readdy-form id="booking-form" className="space-y-7 md:space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors peer" placeholder=" " />
                    <label className="absolute left-0 top-3 text-gray-400 text-base transition-all peer-focus:-top-6 peer-focus:text-gold peer-focus:text-sm peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-sm">Full Name</label>
                  </div>
                  <div className="relative">
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors peer" placeholder=" " />
                    <label className="absolute left-0 top-3 text-gray-400 text-base transition-all peer-focus:-top-6 peer-focus:text-gold peer-focus:text-sm peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-sm">Email Address</label>
                  </div>
                </div>

                <div className="relative">
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors peer" placeholder=" " />
                  <label className="absolute left-0 top-3 text-gray-400 text-base transition-all peer-focus:-top-6 peer-focus:text-gold peer-focus:text-sm peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-sm">Phone Number</label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <input type="text" name="departure" value={formData.departure} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors peer" placeholder=" " />
                    <label className="absolute left-0 top-3 text-gray-400 text-base transition-all peer-focus:-top-6 peer-focus:text-gold peer-focus:text-sm peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-sm">Departure City</label>
                  </div>
                  <div className="relative">
                    <input type="text" name="destination" value={formData.destination} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors peer" placeholder=" " />
                    <label className="absolute left-0 top-3 text-gray-400 text-base transition-all peer-focus:-top-6 peer-focus:text-gold peer-focus:text-sm peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-sm">Destination City</label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <input type="date" name="departureDate" value={formData.departureDate} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors" />
                    <label className="absolute left-0 -top-6 text-gold text-sm">Departure Date</label>
                  </div>
                  <div className="relative">
                    <input type="time" name="departureTime" value={formData.departureTime} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors" />
                    <label className="absolute left-0 -top-6 text-gold text-sm">Departure Time</label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <input type="date" name="returnDate" value={formData.returnDate} onChange={handleChange} className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors" />
                    <label className="absolute left-0 -top-6 text-gray-400 text-sm">Return Date (Optional)</label>
                  </div>
                  <div className="relative">
                    <input type="time" name="returnTime" value={formData.returnTime} onChange={handleChange} className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors" />
                    <label className="absolute left-0 -top-6 text-gray-400 text-sm">Return Time (Optional)</label>
                  </div>
                </div>

                {/* Luggage Section */}
                <div>
                  <p className="text-gold text-sm mb-8">Luggage</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="relative">
                      <select name="luggageType" value={formData.luggageType} onChange={handleChange} className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors cursor-pointer">
                        <option value="" className="bg-charcoal text-gray-400">Select bag type</option>
                        <option value="Carry-on" className="bg-charcoal">Carry-on</option>
                        <option value="Checked Bag" className="bg-charcoal">Checked Bag</option>
                        <option value="Golf Bag" className="bg-charcoal">Golf Bag</option>
                        <option value="Ski Equipment" className="bg-charcoal">Ski Equipment</option>
                        <option value="Oversized Luggage" className="bg-charcoal">Oversized Luggage</option>
                        <option value="Mixed" className="bg-charcoal">Mixed</option>
                      </select>
                      <label className="absolute left-0 -top-6 text-gray-400 text-sm">Bag Type</label>
                    </div>
                    <div className="relative">
                      <select name="luggageCount" value={formData.luggageCount} onChange={handleChange} className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors cursor-pointer">
                        {[...Array(21)].map((_, i) => (
                          <option key={i} value={i} className="bg-charcoal">{i} {i === 1 ? 'Bag' : 'Bags'}</option>
                        ))}
                      </select>
                      <label className="absolute left-0 -top-6 text-gray-400 text-sm">Number of Bags</label>
                    </div>
                  </div>
                </div>

                {/* Catering Section */}
                <div>
                  <p className="text-gold text-sm mb-4">Catering Request</p>
                  <div className="flex gap-4">
                    {['Yes', 'No'].map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, catering: option }))}
                        className={`flex-1 py-3 rounded-full border text-sm font-semibold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                          formData.catering === option
                            ? 'bg-gold border-gold text-black'
                            : 'bg-transparent border-white/30 text-gray-400 hover:border-gold hover:text-white'
                        }`}
                      >
                        {option === 'Yes' ? '🍽️ Yes, please' : '✕ No, thank you'}
                      </button>
                    ))}
                  </div>
                  {formData.catering === 'Yes' && (
                    <p className="text-gray-500 text-xs mt-2">Our team will reach out to discuss your catering preferences.</p>
                  )}
                  <input type="hidden" name="catering" value={formData.catering} />
                </div>

                {/* Flexibility Section */}
                <div>
                  <p className="text-gold text-sm mb-4">Travel Flexibility</p>
                  <div className="flex gap-4">
                    {['Urgent', 'Flexible'].map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, flexibility: option }))}
                        className={`flex-1 py-3 rounded-full border text-sm font-semibold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                          formData.flexibility === option
                            ? 'bg-gold border-gold text-black'
                            : 'bg-transparent border-white/30 text-gray-400 hover:border-gold hover:text-white'
                        }`}
                      >
                        {option === 'Urgent' ? '⚡ Urgent' : '🕐 Flexible'}
                      </button>
                    ))}
                  </div>
                  {formData.flexibility && (
                    <p className="text-gray-500 text-xs mt-2">
                      {formData.flexibility === 'Urgent'
                        ? 'We\'ll prioritize your request for the earliest available departure.'
                        : 'We\'ll find the best options across your preferred travel window.'}
                    </p>
                  )}
                  <input type="hidden" name="flexibility" value={formData.flexibility} />
                </div>

                {/* Pets Section */}
                <div className="relative">
                  <select name="pets" value={formData.pets} onChange={handleChange} className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors cursor-pointer">
                    {[...Array(6)].map((_, i) => (
                      <option key={i} value={i} className="bg-charcoal">{i === 0 ? 'No Pets' : `${i} ${i === 1 ? 'Pet' : 'Pets'}`}</option>
                    ))}
                  </select>
                  <label className="absolute left-0 -top-6 text-gold text-sm">Number of Pets</label>
                </div>

                <div className="relative">
                  <select name="passengers" value={formData.passengers} onChange={handleChange} required className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors cursor-pointer">
                    {[...Array(20)].map((_, i) => (
                      <option key={i + 1} value={i + 1} className="bg-charcoal">{i + 1} {i === 0 ? 'Passenger' : 'Passengers'}</option>
                    ))}
                  </select>
                  <label className="absolute left-0 -top-6 text-gold text-sm">Number of Passengers</label>
                </div>

                <div className="relative">
                  <textarea name="specialRequirements" value={formData.specialRequirements} onChange={handleChange} maxLength={500} rows={4} className="w-full bg-transparent border-b-2 border-white/30 focus:border-gold text-white px-0 py-3 text-base outline-none transition-colors resize-none peer" placeholder=" " />
                  <label className="absolute left-0 top-3 text-gray-400 text-base transition-all peer-focus:-top-6 peer-focus:text-gold peer-focus:text-sm peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-sm">Special Requirements (Optional)</label>
                  <div className="text-right text-sm text-gray-500 mt-1">{formData.specialRequirements.length}/500</div>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full bg-gold text-black py-4 rounded-full text-base font-sans font-semibold tracking-wide hover:bg-gold/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {isSubmitting ? 'SUBMITTING...' : 'SUBMIT REQUEST'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-green-400 px-6 py-4 rounded-lg text-center">
                    Thank you! Your booking request has been submitted. We'll contact you within 24 hours.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-6 py-4 rounded-lg text-center">
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}

                <p className="text-center text-sm text-gray-500">Our team will respond to your request within 24 hours</p>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
